import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  signal,
} from "@angular/core";
import * as Highcharts from "highcharts/highstock";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";
import { HC_RANGE_SELECTOR } from "../../../charts/highcharts-shared";
import moment from "moment";

@Component({
  selector: "app-exp-time-area-chart",
  standalone: true,
  imports: [HighchartsChartComponent],
  encapsulation: ViewEncapsulation.None,
  providers: [
    providePartialHighcharts({
      modules: () => [import("highcharts/esm/modules/stock")],
    }),
  ],
  template: `
    <div class="exp-area-chart" [class.is-empty]="!hasData()">
      @if (loaded() && hasData()) {
        <highcharts-chart
          [options]="chartOptions"
          [constructorType]="'stockChart'"
          style="width:100%; height:100%; display:block;"
        ></highcharts-chart>
      }
      @if (loaded() && !hasData()) {
        <div class="exp-area-empty">No data available.</div>
      }
    </div>
  `,
  styles: [
    `
      .exp-area-chart {
        width: 100%;
        min-height: 220px;
        position: relative;
      }
      .exp-area-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: var(--text-faint);
        font-size: 12px;
      }
      .exp-area-chart .highcharts-background {
        fill: transparent !important;
      }
      .exp-area-chart .highcharts-plot-background {
        fill: transparent !important;
      }
      .exp-area-chart .highcharts-grid .highcharts-grid-line {
        stroke: var(--border);
        stroke-dasharray: 2 3;
        opacity: 0.7;
      }
      .exp-area-chart .highcharts-axis-line {
        stroke: var(--border) !important;
      }
      .exp-area-chart .highcharts-tick {
        stroke: var(--border) !important;
      }
      .exp-area-chart .highcharts-axis-labels text,
      .exp-area-chart .highcharts-yaxis-labels text,
      .exp-area-chart .highcharts-xaxis-labels text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-size: 11px !important;
        font-weight: 500 !important;
      }
      .exp-area-chart .highcharts-range-selector-group text {
        font-family: var(--font-ui) !important;
        font-weight: 500 !important;
        font-size: 11.5px !important;
      }
      .exp-area-chart .highcharts-range-label,
      .exp-area-chart .highcharts-range-input {
        display: none !important;
      }
      .exp-area-chart .highcharts-range-selector-buttons .highcharts-button {
        fill: transparent !important;
        stroke: transparent !important;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button
        .highcharts-button-box {
        fill: var(--surface-2) !important;
        stroke: var(--border) !important;
        stroke-width: 1 !important;
        rx: 999;
        ry: 999;
        transition:
          fill 0.12s,
          stroke 0.12s;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button
        text {
        fill: var(--text-dim) !important;
        transition: fill 0.12s;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button-hover
        .highcharts-button-box {
        fill: var(--surface-2) !important;
        stroke: var(--border-strong) !important;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button-hover
        text {
        fill: var(--text) !important;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button-pressed
        .highcharts-button-box {
        fill: var(--accent-soft) !important;
        stroke: rgba(124, 92, 255, 0.35) !important;
      }
      .exp-area-chart
        .highcharts-range-selector-buttons
        .highcharts-button-pressed
        text {
        fill: var(--accent-2) !important;
        font-weight: 600 !important;
      }
      .exp-area-chart .highcharts-tooltip-box {
        fill: var(--surface-2) !important;
        stroke: var(--border-strong) !important;
        stroke-width: 1 !important;
        fill-opacity: 1 !important;
      }
      .exp-area-chart .highcharts-tooltip text {
        fill: var(--text) !important;
      }
      .exp-area-chart .highcharts-input-group {
        display: none;
      }
      .exp-area-chart .highcharts-credits {
        display: none;
      }
    `,
  ],
})
export class ExpTimeAreaChartComponent implements OnInit, OnChanges {
  /** Time series in [timestamp(ms), value] form. Values are expected to already be in display units. */
  @Input() data: number[][] = [];
  @Input() seriesName = "Portfolio";
  @Input() color = "#7C5CFF";
  /** Optional gradient stops [topColor, bottomColor]. If not provided, falls back to color with alpha. */
  @Input() fillStops: [string, string] | null = null;
  @Input() chartType: "area" | "line" = "area";
  @Input() valueDecimals = 2;
  @Input() valueSuffix = "%";
  @Input() yAxisFormat = "{text}%";
  /** When true (e.g. drawdown), forces the y-axis upper bound to 0. */
  @Input() anchorAtZero = false;
  @Input() height: string | number = 220;
  @Input() showRangeSelector = true;
  @Input() showCrosshair = true;

  loaded = signal(false);
  hasData = signal(false);
  chartOptions: Highcharts.Options = {};

  private dataStart: number | null = null;
  private dataEnd: number | null = null;


  ngOnInit(): void {
    this.initPlot();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.data ||
      changes.seriesName ||
      changes.color ||
      changes.fillStops ||
      changes.chartType ||
      changes.valueDecimals ||
      changes.valueSuffix ||
      changes.yAxisFormat ||
      changes.anchorAtZero ||
      changes.height ||
      changes.showRangeSelector
    ) {
      this.loaded.set(false);
      this.initPlot();
    }
  }

  private initPlot(): void {
    if (!Array.isArray(this.data) || this.data.length < 2) {
      this.hasData.set(false);
      this.loaded.set(true);
      return;
    }
    const sorted = [...this.data].sort((a, b) => a[0] - b[0]);
    this.dataStart = sorted[0][0];
    this.dataEnd = sorted[sorted.length - 1][0];
    this.hasData.set(true);
    this.initChart(sorted);
  }

  private initChart(data: number[][]): void {
    const buttons = this.getAvailableRangeButtons();
    const fillColor: any =
      this.chartType === "area"
        ? {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: this.fillStops
              ? [
                  [0, this.fillStops[0]],
                  [1, this.fillStops[1]],
                ]
              : [
                  [0, this.hexToRgba(this.color, 0.45)],
                  [1, this.hexToRgba(this.color, 0.02)],
                ],
          }
        : undefined;

    const series: any = {
      name: this.seriesName,
      data,
      type: this.chartType,
      color: this.color,
      lineWidth: 2,
      zIndex: 2,
      marker: { enabled: false },
    };
    if (fillColor) series.fillColor = fillColor;

    const valueDecimals = this.valueDecimals;
    const valueSuffix = this.valueSuffix;

    this.chartOptions = {
      chart: {
        height: this.height as any,
        backgroundColor: "transparent",
        spacing: [12, 4, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
        zooming: { mouseWheel: { enabled: false } },
      },
      credits: { enabled: false },
      title: { text: "" },
      navigator: { enabled: false, maskInside: false },
      scrollbar: { enabled: false },
      rangeSelector: this.showRangeSelector
        ? {
            ...HC_RANGE_SELECTOR,
            enabled: true,
            buttons,
            selected: buttons.length - 1,
          }
        : { enabled: false, inputEnabled: false },
      xAxis: {
        type: "datetime",
        lineColor: "transparent",
        tickColor: "transparent",
        crosshair: this.showCrosshair
          ? {
              color: "rgba(124,92,255,0.35)",
              width: 1,
              dashStyle: "ShortDot",
              label: { enabled: false },
            }
          : false,
        labels: { style: { color: "var(--text-dim)" } },
      },
      yAxis: [
        {
          opposite: false,
          gridLineColor: "var(--border)",
          gridLineDashStyle: "ShortDash",
          gridLineWidth: 1,
          minorGridLineWidth: 0,
          title: { text: "" },
          max: this.anchorAtZero ? 0 : undefined,
          labels: {
            format: this.yAxisFormat,
            style: { color: "var(--text-dim)" },
          },
        },
      ],
      legend: { enabled: false },
      tooltip: {
        // Stock charts default to `outside: true`, which appends the tooltip
        // container directly to <body>, escaping `.experience-scope` and the
        // scoped tooltip styles. Keep it inside the chart so theming applies.
        outside: false,
        borderRadius: 8,
        borderWidth: 1,
        padding: 10,
        shadow: false,
        style: { color: "var(--text)" },
        shared: true,
        useHTML: true,
        formatter(this: any) {
          const points = (this.points || []) as any[];
          const date = moment(this.x).format("YYYY-MM-DD");
          const rows = points
            .map(
              (p) =>
                `<div style="display:flex;align-items:center;gap:10px;margin-top:4px;">
                   <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};flex:none;"></span>
                   <span style="color:var(--text-dim);">${p.series.name}</span>
                   <span style="margin-left:auto;font-family:var(--font-mono);font-variant-numeric:tabular-nums;font-weight:600;color:var(--text);">${Number(p.y).toFixed(valueDecimals)}${valueSuffix}</span>
                 </div>`,
            )
            .join("");
          return `<div style="min-width:200px;font-size:12px;color:var(--text);"><div style="font-size:10.5px;letter-spacing:0.06em;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px;font-weight:500;">${date}</div>${rows}</div>`;
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          dataGrouping: { enabled: false },
          marker: {
            enabled: false,
            states: { hover: { enabled: true, radius: 4 } },
          },
          states: { hover: { lineWidthPlus: 0 } },
          label: { enabled: false },
        },
        area: {
          fillOpacity: 1,
          lineWidth: 2,
          threshold: 0,
        },
      },
      series: [series],
    };

    setTimeout(() => {
      this.loaded.set(true);
    });
  }

  private getAvailableRangeButtons(): any[] {
    const allButtons = [
      { type: "month", count: 1, text: "1M", title: "View 1 month" },
      { type: "month", count: 3, text: "3M", title: "View 3 months" },
      { type: "month", count: 6, text: "6M", title: "View 6 months" },
      { type: "ytd", text: "YTD", title: "View year to date" },
      { type: "year", count: 1, text: "1Y", title: "View 1 year" },
      { type: "year", count: 3, text: "3Y", title: "View 3 years" },
      { type: "year", count: 5, text: "5Y", title: "View 5 years" },
      { type: "year", count: 10, text: "10Y", title: "View 10 years" },
      { type: "all", text: "ALL", title: "View all" },
    ];

    if (!this.dataStart || !this.dataEnd) return allButtons;

    const dataSpanMs = this.dataEnd - this.dataStart;
    const oneMonthMs = 30.44 * 24 * 60 * 60 * 1000;
    const oneYearMs = 365.25 * 24 * 60 * 60 * 1000;

    return allButtons.filter((b: any) => {
      if (b.type === "all" || b.type === "ytd") return true;
      if (b.type === "month") return dataSpanMs >= b.count * oneMonthMs;
      if (b.type === "year") return dataSpanMs >= b.count * oneYearMs;
      return true;
    });
  }

  private hexToRgba(hex: string, alpha: number): string {
    const m = /^#?([a-f0-9]{6})$/i.exec(hex.trim());
    if (!m) return hex;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
